/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */
import { useTranslation } from "react-i18next"; // Importing translation hook for text localization
import {useEffect, useLayoutEffect, useRef, useState} from "react"; // Importing React hooks for managing component lifecycle
import { useDispatch, useSelector } from "react-redux"; // Importing Redux hooks to dispatch actions and select state
import { TABLES } from "../../../../Apis/Tables"; // Importing table constants for contract data
import { clearSearchFilters } from "../../../../reduxToolkit/Search/SearchSlice"; // Importing action to clear search filters
import { permissionObj } from "../../../../Helpers/permission"; // Importing permissions helper
import { DeleteItemsApi } from "../../../../reduxToolkit/Commons/CommonsApi"; // Importing API call for deleting items
import { MODELS, MODELS_NAME } from "../../../../Apis/Models";
import { useLocation, useNavigate } from "react-router-dom"; // Importing model constants for contract and contractor data
import { CONTRACTORS_SUBVIEW_MODEL, TABS_CONTRACTORS } from "../Model/Models";
import {VIEW_TYPE} from '../../../../enums/ViewType';
import routes from "../../../../Routes/routes";
import {USER_TYPE_MODEL} from "../../../../enums/userTypeEnum";
import {PROVIDER_SUB_VIEW_MODEL} from "../../../Provider/Model/Model";
import {errorMsg} from "../../../../Apis/functions";

// Main component for contractor panel management
const ContractorPanelApp = () => {
    const { t } = useTranslation(); // Initialize translation function for localization
    const navigate = useNavigate();
    // State variables for managing various aspects of the panel (view, pagination, selected items, etc.)
    const [view, setView] = useState(VIEW_TYPE.LIST.name);
    const [subView, setSubView] = useState("");
    const [activeStep, setActiveStep] = useState(0); // Active step in the workflow
    const [selectContractForDelete, setSelectContractForDelete] = useState([]); // State for selected contracts to delete
    const [deleteContractShow, setDeleteContractShow] = useState(false); // State to show/hide contract deletion confirmation
    const [isAllChecked, setIsAllChecked] = useState(false); // State to track if all contracts are selected for deletion
    const [selectContractorForDelete, setSelectContractorForDelete] = useState([]); // State for selected contractors to delete
    const [selectApprovalFlowForDelete, setSelectApprovalFlowForDelete] = useState([]); // State for selected contractors to delete
    const [deleteContractorShow, setDeleteContractorShow] = useState(false); // State to show/hide contractor deletion confirmation
    const [isAllCheckedContractor, setIsAllCheckedContractor] = useState(false); // State for selecting all contractors for deletion
    const [filterDialogShow, setFilterDialogShow] = useState(false); // State for filter dialog visibility
    const [finalArray, setFinalArray] = useState([]); // Final array to store filtered data
    const [loaded, setLoaded] = useState(false); // State to track if data has been loaded
    const [contractorId, setContractorId] = useState(sessionStorage.getItem('contractorId')); // Contractor ID from session storage
    const [supplierId, setSupplierId] = useState(sessionStorage.getItem('providerId')); // Supplier ID from session storage
    const [importFileShow, setImportFileShow] = useState(false); // State for import file dialog visibility
    const [search, setSearch] = useState(false); // State for search activation
    const [searchElement, setSearchElement] = useState(''); // Search query string
    const [bodyForExport, setBodyForExport] = useState(''); // State for the body of export request
    const [moduleId, setModuleId] = useState(MODELS.Contract); // Module ID for determining which data to show (contract or contractor)
    const [simpleSearch, setSimpleSearch] = useState(false); // State for simple search mode
    const [criteria, setCriteria] = useState(); // Search criteria state
    const [modified, setModified] = useState(false); // State for tracking changes to contracts
    const [deleteApprovalFlowShow, setDeleteApprovalFlowShow] = useState(false);
    const location = useLocation();
    const currentTab = location?.state?.currentTab;
    const currentSubView = location?.state?.currentSubView;
    const viewType = VIEW_TYPE.LIST.viewType
    const userTypeId = USER_TYPE_MODEL.CONTRACTOR_IN_CHARGE

    const btnUpdateRef = useRef();


    let dispatch = useDispatch(); // Initialize dispatch for Redux actions
    const DEV = useSelector((state) => state?.CommonsSlice?.development); // Get development flag from Redux state
    const { permission } = useSelector((state) => state.authenticatioauthennSlice); // Get user permissions from Redux state
    const [steps, setSteps] = useState([]); // Steps in the workflow
    const [option, setOption] = useState(`${TABLES.CONTRACTS}`); // Default table option for contracts

    useLayoutEffect(() => {
        let activeTab = null;
        const stepConfig = [
            {tab: TABS_CONTRACTORS.CONTRACTS.TRANS, perm: [permissionObj?.WEB_CONTRACTOR_CONTRACT_READ], index: TABS_CONTRACTORS.CONTRACTS.INDEX},
            {tab: TABS_CONTRACTORS.CONTRACTORS.TRANS, perm: [permissionObj?.WEB_CONTRACTOR_READ], index: TABS_CONTRACTORS.CONTRACTORS.INDEX},
            {tab: TABS_CONTRACTORS.MANAGE.TRANS, perm: [permissionObj?.WEB_CONTRACTOR_MANAGE_READ], index: TABS_CONTRACTORS.MANAGE.INDEX},
            {tab: TABS_CONTRACTORS.DOCUMENTATION.TRANS, perm:
                    [permissionObj?.WEB_CONTRACTOR_APPROVAL_FLOW_READ,
                     permissionObj?.WEB_CONTRACTOR_REQUEST_READ], index: TABS_CONTRACTORS.DOCUMENTATION.INDEX},
        ]

        const auxStep = stepConfig.reduce((acc, { tab, perm, index }) => {
            const hasPermission = DEV || perm.some(p => permission?.includes(p));
            if (hasPermission) {
                acc.push({ label: t(tab), index: index });
                if (activeTab === null) activeTab = index;
            }
            return acc;
        }, []);

        setSteps(auxStep);
        if (activeTab !== null && !currentTab) handleStepChange(activeTab);
    }, [t, permission])

    useLayoutEffect(() => {
        if(currentTab) handleStepChange(currentTab);
    }, [location]);

    // useEffect to clear search filters when the component mounts
    useEffect(() => {
        return () => {
            dispatch(clearSearchFilters()); // Clear search filters when the component is unmounted
        };
    }, []); // Empty dependency array to run on mount and unmount

    // useEffect to reset checkboxes and clear selection when activeStep or view changes
    useEffect(() => {
        dispatch(clearSearchFilters()); // Clear search filters
        resetAllCheckboxes(); // Reset all checkboxes
        setSelectContractForDelete([]); // Clear contract selection for deletion
        setSelectContractorForDelete([]); // Clear contractor selection for deletion
        setIsAllChecked(false); // Reset "select all" checkbox for contracts
        setIsAllCheckedContractor(false); // Reset "select all" checkbox for contractors
    }, [activeStep, view]); // Depend on activeStep and view

    // Function to reset all checkboxes
    const resetAllCheckboxes = () => {
        const checkboxes = document.querySelectorAll('.checkbox');
        checkboxes.forEach((checkbox) => {
            checkbox.checked = false; // Uncheck each checkbox
        });
    };

    // Function to delete selected contracts
    const deleteSelectedContracts = (deleteItem) => {
        const tableName = MODELS_NAME.contract; // Table name for contracts
        const body = deleteItem; // Data to be deleted

        if (body.length > 0) {
            dispatch(DeleteItemsApi({ tableName, body })).then(
                ({
                    payload: {
                        data: { success },
                    },
                }) => {
                    if (success === true) {
                        setLoaded(true); // Set loaded state to true after successful deletion
                        setSelectContractForDelete([]); // Clear selected contracts for deletion
                        setIsAllChecked(false); // Reset "select all" checkbox
                        setModified(true); // Mark contracts as modified
                    }
                },
            );
        }
    };

    // Function to delete selected contractors
    const deleteSelectedContractors = (deleteItem) => {
        const tableName = MODELS_NAME.contractor; // Table name for contractors
        const body = deleteItem; // Data to be deleted
        if (body.length > 0) {
            dispatch(DeleteItemsApi({ tableName, body })).then(({payload: {data: {  success, code }}}) => {
                    if (success === true) {
                        setLoaded(true); // Set loaded state to true after successful deletion
                        setSelectContractorForDelete([]); // Clear selected contractors for deletion
                        setIsAllCheckedContractor(false); // Reset "select all" checkbox
                        setModified(true); // Mark contractors as modified
                    }else
                        errorMsg(code);
                },
            );
        }
    };

    const handleModuleOption = (step) => {
        switch (step) {
            case TABS_CONTRACTORS.CONTRACTORS.INDEX:
                setModuleId(MODELS.Contractor)
                setOption(TABLES.CONTRACTORS)
                break

            case TABS_CONTRACTORS.CONTRACTS.INDEX:
                setModuleId(MODELS.Contract)
                setOption(TABLES.CONTRACTS)
                break
            case TABS_CONTRACTORS.DOCUMENTATION.INDEX:
                const approval = DEV || permission?.includes(permissionObj.WEB_CONTRACTOR_APPROVAL_FLOW_READ);
                const request =  DEV || permission?.includes(permissionObj.WEB_CONTRACTOR_REQUEST_READ);
                if(currentSubView === CONTRACTORS_SUBVIEW_MODEL.DOCUMENTATION.APPROVAL_FLOW){
                    setModuleId(MODELS.APPROVAL_FLOW)
                    setOption(TABLES.APPROVAL)
                    return;
                }
                if(currentSubView === CONTRACTORS_SUBVIEW_MODEL.DOCUMENTATION.REQUESTS){
                    setModuleId(MODELS.APPROVAL_FLOW)
                    setOption(TABLES.REQUEST_CONTRACTOR)
                    return;
                }
                if( approval && request ){
                    setModuleId(MODELS.APPROVAL_FLOW)
                    setOption(TABLES.APPROVAL)
                }else if(request){
                    setModuleId(MODELS.APPROVAL_FLOW)
                    setOption(TABLES.REQUEST_CONTRACTOR)
                }else if(approval){
                    setModuleId(MODELS.APPROVAL_FLOW)
                    setOption(TABLES.APPROVAL)
                }
                break
        }
    }

    const handleDelete = (step) => {
        switch (step) {
            case TABS_CONTRACTORS.CONTRACTS.INDEX:
                setDeleteContractShow(true)
                break
            case TABS_CONTRACTORS.CONTRACTORS.INDEX:
                setDeleteContractorShow(true)
                break
            case TABS_CONTRACTORS.DOCUMENTATION.INDEX:
                setDeleteApprovalFlowShow(true)
        }
    }

    const handleCreate = (step) => {
        switch (step) {
            case TABS_CONTRACTORS.CONTRACTS.INDEX:
                navigate(routes.employee.createContract)
                break
            case TABS_CONTRACTORS.CONTRACTORS.INDEX:
                navigate(routes.contractor.create)
                break
            case TABS_CONTRACTORS.DOCUMENTATION.INDEX:
                if (subView === CONTRACTORS_SUBVIEW_MODEL.DOCUMENTATION.APPROVAL_FLOW){
                    navigate(routes.contractor.createApprovalLevel)
                }
                else if (subView === PROVIDER_SUB_VIEW_MODEL.DOCUMENTATION.APPROVAL_FLOW){
                    navigate(routes.suppliers.createApprovalLevel)
                }
        }
    }
    // Function to handle changing active step (contract or contractor)
    const handleStepChange = (step) => {
        setSearchElement(''); // Clear search element
        setFinalArray([]);//Clear advanced search
        setActiveStep(step); // Set the active step
        navigate(location.pathname, { replace: true, state: {} });
        handleModuleOption(step)
        subViewChange(step)
    };


    const subViewChange = (step) => {
        if (currentSubView !== undefined && currentSubView !== null) {
            setSubView(currentSubView);
            return;
        }
        switch (step) {
            case TABS_CONTRACTORS.DOCUMENTATION.INDEX:
                const approval = DEV || permission?.includes(permissionObj.WEB_CONTRACTOR_APPROVAL_FLOW_READ);
                const request =  DEV || permission?.includes(permissionObj.WEB_CONTRACTOR_REQUEST_READ);
                const subView = (approval && request)
                    ? CONTRACTORS_SUBVIEW_MODEL.DOCUMENTATION.APPROVAL_FLOW
                    : request
                        ? CONTRACTORS_SUBVIEW_MODEL.DOCUMENTATION.REQUESTS
                        : CONTRACTORS_SUBVIEW_MODEL.DOCUMENTATION.APPROVAL_FLOW
                setSubView(subView);
                break;
        }
    };

    const handleSubViewChange = (view) => {
        if(view === CONTRACTORS_SUBVIEW_MODEL.DOCUMENTATION.APPROVAL_FLOW){
            setOption(TABLES.APPROVAL)
        }
        if(view === CONTRACTORS_SUBVIEW_MODEL.DOCUMENTATION.REQUESTS){
            setOption(TABLES.REQUEST_CONTRACTOR)
        }
        setSubView(view);
        setFinalArray([]);
        setSearchElement("");
    };



    return {
        t, // Translation function for text localization
        navigate,
        DEV,
        permission,
        steps,
        contractorId,
        view,
        setView,
        search,
        option,
        finalArray,
        activeStep,
        setActiveStep,
        setDeleteContractShow,
        criteria,
        setCriteria,
        bodyForExport,
        setBodyForExport,
        searchElement,
        setSearchElement,
        setSearch,
        setSimpleSearch,
        supplierId,
        modified,
        setModified,
        deleteContractShow,
        deleteContractorShow,
        setDeleteContractorShow,
        filterDialogShow,
        setFilterDialogShow,
        moduleId,
        importFileShow,
        setImportFileShow,
        setLoaded,
        isAllChecked,
        setIsAllChecked,
        isAllCheckedContractor,
        setIsAllCheckedContractor,
        selectContractForDelete,
        setSelectContractForDelete,
        selectContractorForDelete,
        setSelectContractorForDelete,
        selectApprovalFlowForDelete,
        setSelectApprovalFlowForDelete,
        setFinalArray,
        handleStepChange,
        deleteSelectedContracts,
        deleteSelectedContractors,
        subView,
        setSubView,
        handleSubViewChange,
        handleDelete,
        btnUpdateRef,
        deleteApprovalFlowShow,
        setDeleteApprovalFlowShow,
        handleCreate,
        viewType,
        userTypeId
    };
}

export default ContractorPanelApp; // Export the component to be used in other parts of the app
